/**
 * Utility class for generating procedural textures
 *
 * IMPORTANT: Uses per-window caching to support same-origin iframes.
 * Each window/iframe context gets its own isolated cache.
 */
export default class TextureGenerator {
    // Per-window cache storage (WeakMap allows GC when windows close)
    static _windowCaches = new WeakMap();

    /**
     * Get the texture cache for the current window context.
     * @returns {Map} Texture cache for this window
     */
    static _getTextureCache() {
      const win = typeof window !== 'undefined' ? window : globalThis;
      if (!this._windowCaches.has(win)) {
        this._windowCaches.set(win, new Map());
      }
      return this._windowCaches.get(win);
    }

    // Convenience accessor for backwards compatibility
    static get textureCache() { return this._getTextureCache(); }

    /**
     * Create a new texture generator
     */
    constructor() {
      this.canvas = document.createElement('canvas');
      this.ctx = this.canvas.getContext('2d');
    }
    
    /**
     * Generate a checkerboard texture
     * @param {number} size - Size of the texture
     * @param {string} color1 - First color
     * @param {string} color2 - Second color
     * @param {number} checks - Number of checks per side
     * @return {string} Data URL of the generated texture
     */
    generateCheckerboard(size = 256, color1 = '#ffffff', color2 = '#000000', checks = 8) {
      // Check cache first
      const cacheKey = `checkerboard_${size}_${color1}_${color2}_${checks}`;
      if (TextureGenerator.textureCache.has(cacheKey)) {
        return TextureGenerator.textureCache.get(cacheKey);
      }

      this.canvas.width = size;
      this.canvas.height = size;

      const checkSize = size / checks;

      for (let y = 0; y < checks; y++) {
        for (let x = 0; x < checks; x++) {
          this.ctx.fillStyle = (x + y) % 2 === 0 ? color1 : color2;
          this.ctx.fillRect(x * checkSize, y * checkSize, checkSize, checkSize);
        }
      }

      const dataURL = this.canvas.toDataURL();
      TextureGenerator.textureCache.set(cacheKey, dataURL);
      return dataURL;
    }
    
    /**
     * Generate a gradient texture
     * @param {number} size - Size of the texture
     * @param {string} color1 - Start color
     * @param {string} color2 - End color
     * @param {string} direction - 'horizontal', 'vertical', or 'radial'
     * @return {string} Data URL of the generated texture
     */
    generateGradient(size = 256, color1 = '#ffffff', color2 = '#000000', direction = 'horizontal') {
      // Check cache first
      const cacheKey = `gradient_${size}_${color1}_${color2}_${direction}`;
      if (TextureGenerator.textureCache.has(cacheKey)) {
        return TextureGenerator.textureCache.get(cacheKey);
      }

      this.canvas.width = size;
      this.canvas.height = size;

      let gradient;

      if (direction === 'horizontal') {
        gradient = this.ctx.createLinearGradient(0, 0, size, 0);
      } else if (direction === 'vertical') {
        gradient = this.ctx.createLinearGradient(0, 0, 0, size);
      } else if (direction === 'radial') {
        gradient = this.ctx.createRadialGradient(
          size / 2, size / 2, 0,
          size / 2, size / 2, size / 2
        );
      } else {
        // Default to horizontal
        gradient = this.ctx.createLinearGradient(0, 0, size, 0);
      }

      gradient.addColorStop(0, color1);
      gradient.addColorStop(1, color2);

      this.ctx.fillStyle = gradient;
      this.ctx.fillRect(0, 0, size, size);

      const dataURL = this.canvas.toDataURL();
      TextureGenerator.textureCache.set(cacheKey, dataURL);
      return dataURL;
    }
    
    /**
     * Generate a noise texture
     * NOTE: Noise is random, so caching only helps when same seed/params
     * @param {number} size - Size of the texture
     * @param {string} color1 - Base color
     * @param {string} color2 - Noise color
     * @param {number} density - Noise density (0-1)
     * @param {number} seed - Optional seed for reproducible noise
     * @return {string} Data URL of the generated texture
     */
    generateNoise(size = 256, color1 = '#ffffff', color2 = '#000000', density = 0.5, seed = null) {
      // Only cache if seed is provided (for reproducible noise)
      if (seed !== null) {
        const cacheKey = `noise_${size}_${color1}_${color2}_${density}_${seed}`;
        if (TextureGenerator.textureCache.has(cacheKey)) {
          return TextureGenerator.textureCache.get(cacheKey);
        }
      }

      this.canvas.width = size;
      this.canvas.height = size;

      // Fill with base color
      this.ctx.fillStyle = color1;
      this.ctx.fillRect(0, 0, size, size);

      // Add noise
      this.ctx.fillStyle = color2;

      // Seed random generator if provided
      if (seed !== null) {
        Math.seedrandom = Math.seedrandom || function(s) {
          const random = function() {
            s = Math.sin(s++) * 10000;
            return s - Math.floor(s);
          };
          return random;
        };
      }

      for (let y = 0; y < size; y++) {
        for (let x = 0; x < size; x++) {
          if (Math.random() < density) {
            this.ctx.fillRect(x, y, 1, 1);
          }
        }
      }

      const dataURL = this.canvas.toDataURL();

      if (seed !== null) {
        const cacheKey = `noise_${size}_${color1}_${color2}_${density}_${seed}`;
        TextureGenerator.textureCache.set(cacheKey, dataURL);
      }

      return dataURL;
    }
    
    /**
     * Generate a grid texture
     * @param {number} size - Size of the texture
     * @param {string} backgroundColor - Background color
     * @param {string} gridColor - Grid line color
     * @param {number} gridSize - Grid cell size
     * @param {number} lineWidth - Grid line width
     * @return {string} Data URL of the generated texture
     */
    generateGrid(size = 256, backgroundColor = '#ffffff', gridColor = '#000000', gridSize = 32, lineWidth = 1) {
      // Check cache first
      const cacheKey = `grid_${size}_${backgroundColor}_${gridColor}_${gridSize}_${lineWidth}`;
      if (TextureGenerator.textureCache.has(cacheKey)) {
        return TextureGenerator.textureCache.get(cacheKey);
      }

      this.canvas.width = size;
      this.canvas.height = size;

      // Fill with background color
      this.ctx.fillStyle = backgroundColor;
      this.ctx.fillRect(0, 0, size, size);

      // Draw grid
      this.ctx.strokeStyle = gridColor;
      this.ctx.lineWidth = lineWidth;

      for (let i = 0; i <= size; i += gridSize) {
        // Vertical lines
        this.ctx.beginPath();
        this.ctx.moveTo(i, 0);
        this.ctx.lineTo(i, size);
        this.ctx.stroke();

        // Horizontal lines
        this.ctx.beginPath();
        this.ctx.moveTo(0, i);
        this.ctx.lineTo(size, i);
        this.ctx.stroke();
      }

      const dataURL = this.canvas.toDataURL();
      TextureGenerator.textureCache.set(cacheKey, dataURL);
      return dataURL;
    }
    
    /**
     * Generate a dot pattern texture
     * @param {number} size - Size of the texture
     * @param {string} backgroundColor - Background color
     * @param {string} dotColor - Dot color
     * @param {number} dotSize - Dot radius
     * @param {number} spacing - Spacing between dots
     * @return {string} Data URL of the generated texture
     */
    generateDots(size = 256, backgroundColor = '#ffffff', dotColor = '#000000', dotSize = 4, spacing = 16) {
      // Check cache first
      const cacheKey = `dots_${size}_${backgroundColor}_${dotColor}_${dotSize}_${spacing}`;
      if (TextureGenerator.textureCache.has(cacheKey)) {
        return TextureGenerator.textureCache.get(cacheKey);
      }

      this.canvas.width = size;
      this.canvas.height = size;

      // Fill with background color
      this.ctx.fillStyle = backgroundColor;
      this.ctx.fillRect(0, 0, size, size);

      // Draw dots
      this.ctx.fillStyle = dotColor;

      for (let y = spacing / 2; y < size; y += spacing) {
        for (let x = spacing / 2; x < size; x += spacing) {
          this.ctx.beginPath();
          this.ctx.arc(x, y, dotSize, 0, Math.PI * 2);
          this.ctx.fill();
        }
      }

      const dataURL = this.canvas.toDataURL();
      TextureGenerator.textureCache.set(cacheKey, dataURL);
      return dataURL;
    }
    
    /**
     * Generate a text texture
     * @param {number} size - Size of the texture
     * @param {string} text - Text to render
     * @param {string} backgroundColor - Background color
     * @param {string} textColor - Text color
     * @param {string} font - Font specification
     * @return {string} Data URL of the generated texture
     */
    generateText(size = 256, text = 'Sample Text', backgroundColor = '#ffffff', textColor = '#000000', font = '32px Arial') {
      // Check cache first
      const cacheKey = `text_${size}_${text}_${backgroundColor}_${textColor}_${font}`;
      if (TextureGenerator.textureCache.has(cacheKey)) {
        return TextureGenerator.textureCache.get(cacheKey);
      }

      this.canvas.width = size;
      this.canvas.height = size;

      // Fill with background color
      this.ctx.fillStyle = backgroundColor;
      this.ctx.fillRect(0, 0, size, size);

      // Draw text
      this.ctx.fillStyle = textColor;
      this.ctx.font = font;
      this.ctx.textAlign = 'center';
      this.ctx.textBaseline = 'middle';

      // Handle multi-line text
      const lines = text.split('\n');
      const lineHeight = parseInt(font) * 1.2;
      const startY = size / 2 - (lines.length - 1) * lineHeight / 2;

      lines.forEach((line, i) => {
        this.ctx.fillText(line, size / 2, startY + i * lineHeight);
      });

      const dataURL = this.canvas.toDataURL();
      TextureGenerator.textureCache.set(cacheKey, dataURL);
      return dataURL;
    }
  }